/**
 * HTTP katmanı — sunucunun TEK hata sözleşmesini sınırda bir kez çözer.
 *
 * Sunucu her hatada `{ code, message?, fields?, traceId }` döner
 * (apps/api/src/common/filters/all-exceptions.filter.ts). Bu şekli burada bir
 * kez `ApiError`a çevirmek, panelin geri kalanının yalnızca `code`a dallanmasını
 * sağlar — `message` dil bağımsız DEĞİLDİR ve çoğu hatada hiç yoktur.
 *
 * axios BİLEREK kullanılmıyor: tek kattığı değer interceptor çiftiydi ve o
 * çift, istek nesnesine `_retry` gibi tip sistemi dışında alanlar iliştirmek
 * zorunda kalıyordu. Ayrıca ağ hatası tespiti axios'un belgelenmemiş string
 * sabitlerine (`ERR_NETWORK`, `ECONNABORTED`) bağlıydı; fetch'te ayrım kesin:
 * ağ çöküşü TypeError fırlatır, HTTP hatası `res.ok === false` olur.
 */

const BASE_URL: string = import.meta.env.VITE_API_URL ?? 'http://127.0.0.1:3011/api';

const ACCESS_KEY = 'centramir.accessToken';
const REFRESH_KEY = 'centramir.refreshToken';

// ─────────────────────────────────────────────────────────────── hata tipleri

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields: string[];
  readonly traceId: string | null;
  /** Sunucunun `message` alanı — YALNIZCA geliştirici/log için. Arayüzde gösterme. */
  readonly serverMessage: string | null;

  constructor(init: {
    code: string;
    status: number;
    fields?: string[];
    traceId?: string | null;
    serverMessage?: string | null;
  }) {
    super(init.code);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.fields = init.fields ?? [];
    this.traceId = init.traceId ?? null;
    this.serverMessage = init.serverMessage ?? null;
  }
}

/** Ağ ulaşılamadı — sunucu hiç cevap vermedi. HTTP hatasından ayrı tutulur. */
export class NetworkError extends Error {
  readonly code = 'NETWORK_ERROR';
  constructor(cause?: unknown) {
    super('NETWORK_ERROR');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * Oturum kurtarılamadı (refresh de başarısız). Router bunu yakalayıp /login'e
 * yönlendirir. Interceptor'ın `window.location.href` ataması BİLEREK yok:
 * global yan etki test edilemez ve happy-dom altında uyarı üretir.
 */
export class AuthExpiredError extends Error {
  readonly code = 'AUTH_EXPIRED';
  constructor() {
    super('AUTH_EXPIRED');
    this.name = 'AuthExpiredError';
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

// ─────────────────────────────────────────────────────────────── token deposu

/*
 * Token'lar localStorage'da. Cookie yolu BİLEREK yok: tek makinede tek
 * operatörün kullandığı bir panelde httpOnly cookie'nin getireceği CSRF
 * yüzeyi ve çift kimlik yolu, XSS'e karşı sağladığı korumadan daha pahalı.
 * Sunucu tarafında da cookie okuyan hiçbir kod yok (yalnız Bearer başlığı).
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// ─────────────────────────────────────────────────────────────── istek

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /**
   * HAM gövde — JSON'a çevrilmeden, olduğu gibi gönderilir (ek yükleme).
   *
   * ⚠ `body` İLE BİRLİKTE KULLANILMAZ ve `rawBody` üstün gelir. İkisi de
   * doluysa hangisinin gideceği çağıranın okuyamayacağı bir ayrıntı olurdu;
   * burada kural tek satırda görünüyor.
   *
   * ⚠ `Content-Type` DOSYANIN KENDİ TİPİDİR ve sunucu ona GÜVENMEZ: ilk
   * baytlarla karşılaştırır (`attachment-rules.ts`). Yani buradan gönderilen
   * tip bir İDDİADIR, bir garanti değil.
   */
  rawBody?: BodyInit;
  contentType?: string;
  /** Kimlik başlığı eklenmesin (login, refresh). */
  anonymous?: boolean;
  signal?: AbortSignal;
  /** 401'de yenileme denenmesin — yenileme isteğinin kendisi için. */
  noRefresh?: boolean;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // Sunucu JSON dönmedi (nginx hata sayfası, proxy zaman aşımı...).
    return { code: 'INVALID_RESPONSE', message: text.slice(0, 200) };
  }
}

function toApiError(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as Record<string, unknown>;
  return new ApiError({
    code: typeof b.code === 'string' ? b.code : `HTTP_${status}`,
    status,
    fields: Array.isArray(b.fields) ? (b.fields as string[]) : [],
    traceId: typeof b.traceId === 'string' ? b.traceId : null,
    serverMessage: typeof b.message === 'string' ? b.message : null,
  });
}

async function rawFetch(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  const raw = opts.rawBody !== undefined;
  if (raw) {
    // Boş dize gönderilmez: `Content-Type` başlığı hiç yoksa sunucu onu boş
    // okur ve beyaz listeye takılır — mesaj yine anlaşılırdır.
    if (opts.contentType) headers['Content-Type'] = opts.contentType;
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (!opts.anonymous) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  try {
    return await fetch(`${BASE_URL}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: raw ? opts.rawBody : opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal ?? null,
    });
  } catch (err) {
    // AbortError'ı yutmayalım — TanStack Query iptali böyle anlıyor.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError(err);
  }
}

// ─────────────────────────────────────────────────────────── token yenileme

/**
 * Tek-uçuş (single-flight) yenileme: aynı anda 401 alan beş istek beş yenileme
 * denemesi yapmaz. Sunucuda yenileme artık ROTASYONLU — eski refresh token
 * blocklist'e girer; paralel iki yenileme ikincisini geçersiz kılardı.
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    try {
      const res = await rawFetch('/auth/refresh', {
        method: 'POST',
        body: { refreshToken },
        anonymous: true,
        noRefresh: true,
      });
      if (!res.ok) return false;
      const body = (await parseBody(res)) as {
        accessToken?: string;
        refreshToken?: string;
      } | null;
      if (!body?.accessToken || !body.refreshToken) return false;
      setTokens(body.accessToken, body.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Sonraki 401 yeni bir yenileme başlatabilsin.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

// ─────────────────────────────────────────────────────────────── genel giriş

/**
 * İsteği atar, 401'de BİR KEZ yeniler ve HAM `Response`u döndürür — gövdeyi
 * okumaz.
 *
 * JSON uçları `apiFetch`, ikili indirmeler `apiFetchFile` kendi okumasını
 * yapar. Yenileme mantığının burada TEK kopya kalması şart: ikinci bir kopya,
 * rotasyonlu refresh altında iki paralel yenileme demektir ve sunucu ikincisini
 * TÜM AİLEYİ düşürerek reddeder — kullanıcı hiçbir sebep göremeden oturumdan
 * atılır.
 */
async function fetchWithRefresh(path: string, opts: RequestOptions): Promise<Response> {
  let res = await rawFetch(path, opts);

  if (res.status === 401 && !opts.noRefresh && !opts.anonymous) {
    const ok = await refreshTokens();
    if (!ok) {
      clearTokens();
      throw new AuthExpiredError();
    }
    res = await rawFetch(path, opts);
    if (res.status === 401) {
      clearTokens();
      throw new AuthExpiredError();
    }
  }

  return res;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await fetchWithRefresh(path, opts);
  const body = await parseBody(res);
  if (!res.ok) throw toApiError(res.status, body);
  return body as T;
}

// ────────────────────────────────────────────────────────────── dosya indirme

export interface DownloadedFile {
  blob: Blob;
  /** Sunucunun `Content-Disposition`da verdiği ad; yoksa `null`. */
  fileName: string | null;
}

/**
 * `Content-Disposition` başlığından dosya adı.
 *
 * ⚠ AD SUNUCUNUNDUR, istemcide yeniden türetilmez. E-fatura XML'inin adı
 * sunucuda belge numarasından ASCII'ye indirgenerek üretilir
 * (`einvoice.service.ts#renderXml`; ham numara `/` ve `..` içerebilir) ve
 * operatör o dosyayı sağlayıcı portalına yükler. İstemcide ikinci bir ad
 * üretmek, portala yüklenen dosyanın adıyla kayıtlarımızdaki adın ayrışması
 * demektir.
 */
function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null;

  // RFC 5987 biçimi önce: bir gün ASCII dışı bir ad üretilirse doğrusu odur.
  const extended = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  const extendedValue = extended?.[1];
  if (extendedValue !== undefined) {
    try {
      return decodeURIComponent(extendedValue.trim().replace(/^"|"$/g, ''));
    } catch {
      // Bozuk yüzde kodlaması — düz `filename`a düşülür.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() ?? null;
}

/**
 * İKİLİ indirme — kimlik başlığıyla.
 *
 * ⚠ NEDEN DÜZ BİR `<a href>` DEĞİL: panel token'ı `localStorage`dadır ve cookie
 * yolu bilerek yoktur. Tarayıcının kendi indirmesine `Authorization` başlığı
 * eklenemez; bağlantıya tıklamak 401 döndürür ve operatör sebebini hiçbir
 * yerde göremez. Baytlar bu yüzden burada çekilir.
 *
 * ⚠ Hata yolu `apiFetch` ile AYNI: hata gövdesi yine tek sözleşmedir
 * (`{ code, message?, fields?, traceId }`) ve `ApiError`a çevrilir. Aksi hâlde
 * çağıran, 409 `EINVOICE_ALREADY_DELIVERED` gibi ANLAMLI bir reddi "indirme
 * başarısız" diye okur ve onay adımı hiç çalışmazdı.
 */
export async function apiFetchFile(
  path: string,
  opts: Omit<RequestOptions, 'body'> = {},
): Promise<DownloadedFile> {
  const res = await fetchWithRefresh(path, opts);
  if (!res.ok) {
    const body = await parseBody(res);
    throw toApiError(res.status, body);
  }
  return {
    blob: await res.blob(),
    fileName: fileNameFromDisposition(res.headers.get('content-disposition')),
  };
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
  /**
   * HAM gövdeli POST — ek yükleme (fiş görüntüsü, belge eki).
   *
   * ⚠ `FormData` DEĞİL. Sunucu multipart beklemiyor ve bu bir tercih değil:
   * multipart, kullanıcı girdisinden gelen bir DOSYA ADI taşır; arşivdeki ad
   * ise uuid'den türer ve uzantı sabittir. Alınmayan bir girdi kötüye
   * kullanılamaz.
   */
  postRaw: <T>(path: string, body: BodyInit, contentType: string) =>
    apiFetch<T>(path, { method: 'POST', rawBody: body, contentType }),
  /** Kimlik başlıklı İKİLİ indirme (XML, ileride PDF). Gövde JSON DEĞİLDİR. */
  file: (path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetchFile(path, { ...opts, method: 'GET' }),
};
